import { components, useApi } from "@appretio/api";
import { ContextField } from "@appretio/context-selector";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { forecastContextSelectorQueryKeys } from "./queryKeys";

interface UseForecastFilteredContextOptionsParams {
  fields?: ContextField[];
  cubeIds?: string[];
  filters?: components["schemas"]["ForecastContextOptionsInput"]["filters"];
  enabled?: boolean;
}

/** Transform null values to undefined to match the expected type. */
function nullValuesToUndefined(result: Record<string, string[] | null>) {
  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => [
      key,
      value === null ? undefined : value
    ])
  );
}

export function useForecastFilteredContextOptions({
  fields,
  cubeIds,
  filters,
  enabled
}: UseForecastFilteredContextOptionsParams) {
  const client = useApi();

  // Strip empty filter values
  const cleanedFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(
      ([, values]) => Array.isArray(values) && values.some((v) => v !== "")
    )
  );

  const isEnabled =
    (enabled ?? true) &&
    (Object.values(filters ?? {}).some((v) => v && v.length > 0) ||
      (cubeIds != null && cubeIds.length > 0));

  // With no effective filters the request is byte-identical to the base
  // hook's (the backend treats empty `fields` as all fields), so adopt its
  // query key — TanStack dedupes the two observers into a single request.
  // Only while enabled: a disabled observer must keep returning
  // `undefined` rather than surface the base hook's cache.
  const serveBase = isEnabled && Object.keys(cleanedFilters).length === 0;

  return useQuery({
    queryKey: serveBase
      ? forecastContextSelectorQueryKeys.base(cubeIds ?? [])
      : forecastContextSelectorQueryKeys.list(
          JSON.stringify({ fields, cubeIds, filters: cleanedFilters })
        ),
    meta: {
      errorMessage: "Failed to fetch filtered forecast context options"
    },
    queryFn: async () => {
      const response = await client.POST(
        "/v2/forecast/metadata/context-options",
        {
          body: {
            fields: serveBase ? [] : ((fields || []) as string[]),
            cube_ids: cubeIds || [],
            filters: cleanedFilters
          }
        }
      );

      return response.data?.result ?? {};
    },
    // As `select` (not in the queryFn) so a shared base cache entry keeps
    // the raw shape the base hook stores, whichever observer fetched it.
    select: nullValuesToUndefined,
    enabled: isEnabled,
    // Mirror the base hook's staleTime while sharing its key, so this
    // observer doesn't mark the shared entry stale and refetch on mount.
    staleTime: serveBase ? Infinity : 0,
    // Option list: the key changes on every filter click; keep the previous
    // options visible while the narrowed set fetches so the dropdowns don't
    // flash empty mid-interaction.
    placeholderData: keepPreviousData
  });
}
